<template>
  <div v-if="isVisible" class="pfea-overlay" @click.self="close" role="dialog" aria-modal="true">

    <div class="pfea-dialog" ref="modalContent" tabindex="-1">

      <!-- ═══ HEADER ═══ -->
      <div class="pfea-head">
        <div class="pfea-head-left">
          <div class="pfea-head-icon"><i class="ri-time-line"></i></div>
          <div>
            <div class="pfea-head-title">Commandes en file d'attente</div>
            <div class="pfea-head-sub">
              <span class="pfea-chip">{{ filteredData.length }} commande{{ filteredData.length > 1 ? 's' : '' }}</span>
            </div>
          </div>
        </div>
        <button class="pfea-close" @click="close" aria-label="Fermer">
          <i class="ri-close-line"></i>
        </button>
      </div>

      <!-- ═══ BARRE OUTILS ═══ -->
      <div class="pfea-toolbar">
        <div class="pfea-search-wrap">
          <i class="ri-search-line pfea-search-icon"></i>
          <input
            v-model="searchQuery"
            type="search"
            class="pfea-search"
            placeholder="Rechercher par client, n° doc, table..."
            @keyup.enter="selectFirstResult"
          />
          <button v-if="searchQuery" @click="clearSearch" class="pfea-search-clear">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="pfea-perpage">
          <span class="pfea-perpage-lbl">Afficher</span>
          <select v-model="perPage" class="pfea-perpage-sel">
            <option :value="5">5</option>
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
          </select>
        </div>
      </div>

      <!-- ═══ COMPTEUR RÉSULTATS ═══ -->
      <div class="pfea-count-bar">
        <span class="pfea-count-num">{{ filteredData.length }}</span>
        résultat{{ filteredData.length > 1 ? 's' : '' }}
        <span v-if="searchQuery"> pour "{{ searchQuery }}"</span>
      </div>

      <!-- ═══ BODY ═══ -->
      <div class="pfea-body">

        <!-- Loading -->
        <div v-if="isLoading" class="pfea-state">
          <i class="ri-loader-4-line pfea-state-spin pfea-state-icon"></i>
          <p>Chargement des commandes…</p>
        </div>

        <!-- Error -->
        <div v-else-if="errorMessage" class="pfea-state">
          <i class="ri-error-warning-line pfea-state-icon pfea-state-err"></i>
          <p>{{ errorMessage }}</p>
          <button class="pfea-btn-sec" @click="refreshData">
            <i class="ri-refresh-line"></i> Réessayer
          </button>
        </div>

        <!-- Table desktop -->
        <table v-else class="pfea-table">
          <thead>
            <tr>
              <th class="pfea-th pfea-th-num">N°</th>
              <th class="pfea-th">N° Document</th>
              <th class="pfea-th">Client</th>
              <th class="pfea-th pfea-col-hide-sm">Table</th>
              <th class="pfea-th pfea-col-hide-sm">Serveur</th>
              <th class="pfea-th pfea-th-right">Montant</th>
              <th class="pfea-th pfea-col-hide-md">Créé par</th>
              <th class="pfea-th pfea-col-hide-md">Date</th>
              <th class="pfea-th pfea-th-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="paginatedData.length">
              <tr
                v-for="(item, index) in paginatedData"
                :key="item.numdoc || index"
                class="pfea-tr"
                @dblclick="selectOrder(item)"
              >
                <td class="pfea-td">
                  <span class="pfea-num-badge">{{ (currentPage - 1) * perPage + index + 1 }}</span>
                </td>
                <td class="pfea-td">
                  <span class="pfea-doc-badge">{{ item.numdoc }}</span>
                </td>
                <td class="pfea-td">
                  <div class="pfea-client-row">
                    <div class="pfea-avatar">{{ getInitials(item.client) }}</div>
                    <span class="pfea-client-name">{{ item.client || '-' }}</span>
                  </div>
                </td>
                <td class="pfea-td pfea-col-hide-sm">
                  <span v-if="item.commandetable" class="pfea-table-badge">
                    <i class="ri-layout-grid-line"></i> {{ item.commandetable }}
                  </span>
                  <span v-else class="pfea-dash">-</span>
                </td>
                <td class="pfea-td pfea-col-hide-sm">
                  <span class="pfea-text-muted">{{ item.serveur || '-' }}</span>
                </td>
                <td class="pfea-td pfea-td-right">
                  <span class="pfea-amount">{{ formatCurrency(item.mtntcc) }}</span>
                </td>
                <td class="pfea-td pfea-col-hide-md pfea-text-muted">{{ item.saisiepar || '-' }}</td>
                <td class="pfea-td pfea-col-hide-md pfea-text-muted">{{ item.creerler || '-' }}</td>
                <td class="pfea-td pfea-td-center">
                  <button class="pfea-load-btn" @click.stop="selectOrder(item)">
                    <i class="ri-upload-2-line"></i>
                    <span>Charger</span>
                  </button>
                </td>
              </tr>
            </template>
            <tr v-else>
              <td colspan="9">
                <div class="pfea-state">
                  <i class="ri-file-list-3-line pfea-state-icon"></i>
                  <p>{{ searchQuery ? 'Aucun résultat' : 'La file d\'attente est vide' }}</p>
                  <small v-if="searchQuery">Essayez de modifier votre recherche</small>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

      </div>

      <!-- ═══ FOOTER / PAGINATION ═══ -->
      <div class="pfea-foot">
        <div class="pfea-page-info">
          Page <strong>{{ currentPage }}</strong> / <strong>{{ totalPages || 1 }}</strong>
          <span class="pfea-page-total">({{ filteredData.length }} élément{{ filteredData.length > 1 ? 's' : '' }})</span>
        </div>
        <div class="pfea-page-nav">
          <button
            class="pfea-page-btn"
            @click="currentPage--"
            :disabled="currentPage === 1"
          ><i class="ri-arrow-left-s-line"></i></button>

          <template v-for="pageNum in displayedPages" :key="pageNum">
            <span v-if="pageNum === '...'" class="pfea-page-dots">…</span>
            <button
              v-else
              class="pfea-page-btn"
              :class="{ 'pfea-page-btn-active': currentPage === pageNum }"
              @click="currentPage = pageNum"
            >{{ pageNum }}</button>
          </template>

          <button
            class="pfea-page-btn"
            @click="currentPage++"
            :disabled="currentPage >= totalPages"
          ><i class="ri-arrow-right-s-line"></i></button>

          <button class="pfea-btn-sec" @click="close">Fermer</button>
        </div>
      </div>

    </div>

    <!-- Child modal suppression -->
    <ModalFormSuppression ref="modalFormSuppression" @closed="refreshData" />

  </div>
</template>

<script>
    import axios from 'axios';
    import { currencyFormatDE, randomString } from '@/allfunction';
    import ModalFormSuppression from '@/components/views/pages/gestioncommerciale/achatvente/default/formsuppression';
    import * as JwtService from '@/common/jwt.service';

    export default {
        /* eslint-disable */
        name: 'OrderQueueModal',

        components: {
            ModalFormSuppression,
        },

        emits: ['selectedfacture'],

        data() {
            return {
                // Visibility & State
                isVisible: false,
                isLoading: false,
                errorMessage: '',

                // Data
                dataTable: [],

                // Search & Pagination
                searchQuery: '',
                currentPage: 1,
                perPage: 10,

                // Debounce timer
                searchDebounceTimer: null,
            };
        },

        computed: {
            filteredData() {
                if (!this.searchQuery.trim()) {
                    return this.dataTable;
                }

                const query = this.searchQuery.toLowerCase().trim();
                return this.dataTable.filter(item => {
                    const client = item.client?.toLowerCase() || '';
                    const numdoc = item.numdoc?.toLowerCase() || '';
                    const table = item.commandetable?.toLowerCase() || '';
                    const serveur = item.serveur?.toLowerCase() || '';
                    return (
                        client.includes(query) ||
                        numdoc.includes(query) ||
                        table.includes(query) ||
                        serveur.includes(query)
                    );
                });
            },

            paginatedData() {
                const start = (this.currentPage - 1) * this.perPage;
                return this.filteredData.slice(start, start + this.perPage);
            },

            totalPages() {
                return Math.ceil(this.filteredData.length / this.perPage) || 1;
            },

            displayedPages() {
                const pages = [];
                const total = this.totalPages;
                const current = this.currentPage;

                if (total <= 7) {
                    for (let i = 1; i <= total; i++) pages.push(i);
                } else {
                    if (current <= 4) {
                        pages.push(1, 2, 3, 4, 5, '...', total);
                    } else if (current >= total - 3) {
                        pages.push(1, '...', total - 4, total - 3, total - 2, total - 1, total);
                    } else {
                        pages.push(1, '...', current - 1, current, current + 1, '...', total);
                    }
                }

                return pages;
            },
        },

        watch: {
            searchQuery() {
                clearTimeout(this.searchDebounceTimer);
                this.searchDebounceTimer = setTimeout(() => {
                    this.currentPage = 1;
                }, 150);
            },

            perPage() {
                this.currentPage = 1;
            },
        },

        methods: {
            // ============ MODAL METHODS ============
            show(dataTable = []) {
                this.dataTable = dataTable;
                this.searchQuery = '';
                this.currentPage = 1;
                this.errorMessage = '';
                this.isVisible = true;
                document.body.style.overflow = 'hidden';

                this.$nextTick(() => {
                    this.$refs.modalContent?.focus();
                });
            },

            close() {
                this.isVisible = false;
                document.body.style.overflow = '';
            },

            // ============ DATA METHODS ============
            async refreshData() {
                this.isLoading = true;
                this.errorMessage = '';

                try {
                    const caisse = {
                        cf: this.$store.state.caisseinfo,
                        document: 'FV',
                    };

                    const url = `${process.env.VUE_APP_API_URL}/egl74587kjkjrfdkcaisse771714dfe/comptoire/caisse/${this.$store.state.caisseencour}/${this.$store.state.appsociete}/load895418init51comptoirerefdepdpcc11515/option2/${randomString(280)}`;

                    const response = await axios.post(url, caisse, {
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${JwtService.getToken()}`,
                            'csrftoken': this.$store.state.appsociete,
                        },
                        withCredentials: true,
                    });

                    if (response.data.success) {
                        this.dataTable = response.data.m || [];
                    } else {
                        this.dataTable = [];
                        this.errorMessage = response.data.message || 'Erreur lors du chargement des données';
                    }
                } catch (error) {
                    console.error('Erreur lors du chargement:', error);
                    this.errorMessage = 'Impossible de charger les commandes. Vérifiez votre connexion.';
                    this.dataTable = [];
                } finally {
                    this.isLoading = false;
                }
            },

            // ============ SELECTION METHODS ============
            selectOrder(item) {
                this.$emit('selectedfacture', { item });
                this.close();
            },

            selectFirstResult() {
                if (this.paginatedData.length > 0) {
                    this.selectOrder(this.paginatedData[0]);
                }
            },

            clearSearch() {
                this.searchQuery = '';
            },

            // ============ DELETE METHOD ============
            confirmDelete(item) {
                if (parseFloat(item.nuse) === 1) {
                    alert('Impossible de supprimer cette commande.');
                    return;
                }
                const title = `Voulez-vous vraiment supprimer le document ${item.numdoc} ?`;
                this.$refs.modalFormSuppression.show(title, item, 'F', 'vente');
            },

            // ============ UTILITIES ============
            formatCurrency(value) {
                return currencyFormatDE(value);
            },

            getInitials(name) {
                if (!name) return '?';
                return name
                    .split(' ')
                    .map(word => word[0])
                    .join('')
                    .substring(0, 2)
                    .toUpperCase();
            },

            // ============ KEYBOARD HANDLER ============
            handleKeydown(event) {
                if (event.key === 'Escape' && this.isVisible) {
                    this.close();
                }
            },
        },

        mounted() {
            document.addEventListener('keydown', this.handleKeydown);
        },

        beforeUnmount() {
            document.removeEventListener('keydown', this.handleKeydown);
            document.body.style.overflow = '';
            clearTimeout(this.searchDebounceTimer);
        },
    };
</script>

<style scoped>
/* ── Overlay ── */
.pfea-overlay {
  position: fixed; inset: 0; z-index: 10030;
  background: rgba(0, 0, 0, .55);
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
  font-family: 'Segoe UI', system-ui, sans-serif;
}

/* ── Dialog ── */
.pfea-dialog {
  background: var(--surface); border: 1px solid var(--border);
  border-radius: 14px; width: min(860px, 100%);
  max-height: 90dvh; display: flex; flex-direction: column;
  overflow: hidden; box-shadow: 0 24px 60px rgba(0, 0, 0, .45);
  animation: pfea-in .22s cubic-bezier(.4, 0, .2, 1);
  outline: none;
}
@keyframes pfea-in {
  from { opacity: 0; transform: scale(.96) translateY(10px); }
  to   { opacity: 1; transform: none; }
}

/* ── Header ── */
.pfea-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 1px solid var(--border); flex-shrink: 0;
}
.pfea-head-left { display: flex; align-items: center; gap: 10px; }
.pfea-head-icon {
  width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
  background: color-mix(in srgb, var(--amber) 15%, transparent);
  color: var(--amber); font-size: 18px;
  display: flex; align-items: center; justify-content: center;
}
.pfea-head-title { font-size: 14px; font-weight: 800; color: var(--text); }
.pfea-head-sub   { margin-top: 2px; }
.pfea-chip {
  display: inline-block; font-size: 10px; font-weight: 700; padding: 2px 8px;
  border-radius: 20px; border: 1px solid var(--amber);
  color: var(--amber);
  background: color-mix(in srgb, var(--amber) 10%, transparent);
}
.pfea-close {
  background: none; border: none; color: var(--text2); font-size: 20px;
  cursor: pointer; line-height: 1; padding: 4px; border-radius: 6px;
  flex-shrink: 0; transition: color .12s;
}
.pfea-close:hover { color: var(--red); }

/* ── Toolbar ── */
.pfea-toolbar {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  padding: 10px 14px; border-bottom: 1px solid var(--border); flex-shrink: 0;
}
.pfea-search-wrap { position: relative; flex: 1; min-width: 180px; }
.pfea-search-icon {
  position: absolute; left: 10px; top: 50%; transform: translateY(-50%);
  color: var(--text3); pointer-events: none; font-size: 15px;
}
.pfea-search {
  width: 100%; padding: 9px 34px 9px 32px;
  background: var(--surface2); border: 1px solid var(--border);
  border-radius: 8px; color: var(--text); font-size: 13px;
  outline: none; font-family: inherit; transition: border-color .15s;
}
.pfea-search:focus { border-color: var(--amber); }
.pfea-search::placeholder { color: var(--text3); }
.pfea-search-clear {
  position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
  background: none; border: none; color: var(--text2); cursor: pointer;
  font-size: 14px; padding: 0;
}
.pfea-perpage { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.pfea-perpage-lbl { font-size: 11px; font-weight: 700; color: var(--text3); white-space: nowrap; }
.pfea-perpage-sel {
  padding: 7px 10px; border-radius: 7px;
  background: var(--surface2); border: 1px solid var(--border);
  color: var(--text); font-size: 12px; outline: none; cursor: pointer; font-family: inherit;
}

/* ── Compteur ── */
.pfea-count-bar {
  padding: 7px 18px; font-size: 12px; color: var(--text2);
  background: var(--surface2); border-bottom: 1px solid var(--border); flex-shrink: 0;
}
.pfea-count-num { font-weight: 800; color: var(--text); }

/* ── Body (scrollable) ── */
.pfea-body {
  flex: 1; overflow-y: auto;
  scrollbar-width: thin; scrollbar-color: var(--border) transparent;
}
.pfea-body::-webkit-scrollbar { width: 5px; }
.pfea-body::-webkit-scrollbar-track { background: transparent; }
.pfea-body::-webkit-scrollbar-thumb { background: var(--border); border-radius: 3px; }

/* ── States (loading/error/empty) ── */
.pfea-state {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 60px 20px; text-align: center; gap: 10px;
}
.pfea-state-icon { font-size: 40px; color: var(--text3); }
.pfea-state-spin { animation: pfea-spin .9s linear infinite; }
.pfea-state-err  { color: var(--red); }
@keyframes pfea-spin { to { transform: rotate(360deg); } }
.pfea-state p    { font-size: 14px; font-weight: 600; color: var(--text2); }
.pfea-state small { font-size: 11px; color: var(--text3); }

/* ── Table ── */
.pfea-table { width: 100%; border-collapse: collapse; }
.pfea-th {
  padding: 10px 14px; text-align: left; font-size: 10px; font-weight: 800;
  text-transform: uppercase; letter-spacing: .5px; color: var(--text2);
  background: color-mix(in srgb, var(--amber) 10%, var(--surface2));
  border-bottom: 1px solid var(--border); position: sticky; top: 0; z-index: 1;
  white-space: nowrap;
}
.pfea-th-num    { width: 44px; }
.pfea-th-right  { text-align: right; }
.pfea-th-center { text-align: center; }

.pfea-tr {
  border-bottom: 1px solid var(--border); cursor: pointer;
  transition: background .1s;
}
.pfea-tr:hover { background: color-mix(in srgb, var(--amber) 6%, var(--surface)); }
.pfea-tr:last-child { border-bottom: none; }

.pfea-td {
  padding: 10px 14px; font-size: 12px; color: var(--text);
  vertical-align: middle;
}
.pfea-td-right  { text-align: right; }
.pfea-td-center { text-align: center; }

.pfea-num-badge {
  display: inline-flex; align-items: center; justify-content: center;
  width: 24px; height: 24px; border-radius: 50%; font-size: 11px; font-weight: 800;
  background: var(--surface2); color: var(--text2);
}
.pfea-tr:hover .pfea-num-badge {
  background: color-mix(in srgb, var(--amber) 20%, transparent);
  color: var(--amber);
}
.pfea-doc-badge {
  display: inline-block; padding: 3px 8px; border-radius: 6px;
  background: var(--surface2); border: 1px solid var(--border);
  font-size: 11px; font-weight: 700; font-family: ui-monospace, monospace; color: var(--text);
}
.pfea-client-row { display: flex; align-items: center; gap: 8px; }
.pfea-avatar {
  width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0;
  background: var(--blue); color: #fff;
  font-size: 10px; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
}
.pfea-client-name { font-size: 12px; font-weight: 600; color: var(--text); max-width: 140px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pfea-table-badge {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 8px; border-radius: 6px; font-size: 11px; font-weight: 700;
  background: color-mix(in srgb, var(--purple) 12%, transparent);
  color: var(--purple);
}
.pfea-amount { font-size: 13px; font-weight: 800; color: var(--green); }
.pfea-text-muted { font-size: 11px; color: var(--text2); }
.pfea-dash { color: var(--text3); }

.pfea-load-btn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 6px 12px; border-radius: 7px; border: none; cursor: pointer;
  background: color-mix(in srgb, var(--amber) 15%, transparent);
  color: var(--amber); font-size: 12px; font-weight: 700;
  font-family: inherit; transition: background .1s;
}
.pfea-load-btn:hover { background: color-mix(in srgb, var(--amber) 25%, transparent); }

/* Hide cols on smaller screens */
.pfea-col-hide-sm { display: table-cell; }
.pfea-col-hide-md { display: table-cell; }

/* ── Footer / Pagination ── */
.pfea-foot {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 12px 14px; border-top: 1px solid var(--border); flex-shrink: 0;
  background: color-mix(in srgb, var(--surface2) 50%, var(--surface));
  flex-wrap: wrap;
}
.pfea-page-info {
  font-size: 12px; color: var(--text2);
}
.pfea-page-info strong { color: var(--text); }
.pfea-page-total { color: var(--text3); margin-left: 6px; }
.pfea-page-nav { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }

.pfea-page-btn {
  min-width: 32px; height: 32px; padding: 0 6px; border-radius: 7px;
  background: var(--surface2); border: 1px solid var(--border);
  color: var(--text2); font-size: 12px; font-weight: 700;
  cursor: pointer; font-family: inherit; transition: all .1s;
  display: flex; align-items: center; justify-content: center;
}
.pfea-page-btn:hover:not(:disabled) { border-color: var(--amber); color: var(--amber); }
.pfea-page-btn:disabled { opacity: .35; cursor: not-allowed; }
.pfea-page-btn-active {
  background: var(--amber) !important; border-color: var(--amber) !important;
  color: #fff !important;
}
.pfea-page-dots { font-size: 12px; color: var(--text3); padding: 0 4px; }

.pfea-btn-sec {
  padding: 7px 16px; border-radius: 8px; margin-left: 8px;
  background: var(--surface2); border: 1px solid var(--border);
  color: var(--text2); font-size: 13px; font-weight: 600;
  cursor: pointer; font-family: inherit; transition: all .12s;
}
.pfea-btn-sec:hover { border-color: var(--red); color: var(--red); }

/* ── Mobile bottom-sheet ── */
@media (max-width: 640px) {
  .pfea-overlay { align-items: flex-end; padding: 0; }
  .pfea-dialog {
    width: 100%; max-height: 94dvh;
    border-radius: 16px 16px 0 0;
    animation: pfea-slide-up .25s cubic-bezier(.4, 0, .2, 1);
  }
  .pfea-col-hide-sm { display: none; }
  .pfea-col-hide-md { display: none; }
  .pfea-page-total  { display: none; }
}
@keyframes pfea-slide-up {
  from { transform: translateY(100%); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}

@media (max-width: 860px) {
  .pfea-col-hide-md { display: none; }
}
</style>
